import { FC, useMemo, useState } from 'react'
import {
  Box,
  ButtonBase,
  Button,
  Typography,
  Chip,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  Link,
  useMediaQuery
} from '@mui/material'
import { ArrowForward, Check, MeetingRoomOutlined } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { v4 as uuidv4 } from 'uuid'
import { useSnackbar } from 'notistack'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'

import BackNavigationHeader, { BackNavigationContent } from 'components/BackNavigationHeader'
import MobileBottomFooter from 'components/MobileBottomFooter'
import {
  FieldLabel,
  StepIndicator,
  primaryButtonSx,
  submitButtonSx,
  optionChipSx,
  calendarCardSx,
  calendarSx
} from 'components/Helpdesk/FormPrimitives'
import {
  CommonRoom,
  useCommonRoomsList,
  useCommonRoomAvailability,
  useCreateCommonRoomReservation,
  useCommonRoomsReservationsList
} from 'services/commonRooms/commonRooms.hooks'
import { getEquipmentList } from 'services/commonRooms/commonRooms.utils'
import { useUsersProfile } from 'services/profile/profile.hooks'
import theme from 'styles/theme'

dayjs.extend(localizedFormat)
dayjs.extend(isSameOrBefore)

const MINIMUM_SLOT_WITH_MAINTENANCE_MINUTES = 60
const MAINTENANCE_MINUTES = 15

// Booking length options based on the room's slot duration and booking limit.
const getAvailableSlotsDuration = (space: CommonRoom) => {
  const slotDurationMinutes = space.slotDurationMinutes || 30
  const maxBookingHours = space.maxBookingHours || 2
  const maxMinutes = maxBookingHours * 60

  const slots: { hours: number; minutes: number; duration: number; hasMaintanceTime: boolean }[] =
    []

  for (
    let duration = slotDurationMinutes;
    duration <= maxMinutes;
    duration += slotDurationMinutes
  ) {
    const hours = Math.floor(duration / 60)
    const minutes = duration % 60
    const hasMaintanceTime = duration < MINIMUM_SLOT_WITH_MAINTENANCE_MINUTES
    const durationWithMaintenance = hasMaintanceTime ? duration + MAINTENANCE_MINUTES : duration

    slots.push({ hours, minutes, duration: durationWithMaintenance, hasMaintanceTime })
  }

  return slots
}

const getDefaultSlotsDuration = (space: CommonRoom) =>
  space.slotDurationMinutes < MINIMUM_SLOT_WITH_MAINTENANCE_MINUTES
    ? space.slotDurationMinutes + MAINTENANCE_MINUTES
    : space.slotDurationMinutes

const formatDurationLabel = (hours: number, minutes: number) => {
  if (hours === 0) {
    return `${minutes} min`
  }
  if (minutes === 0) {
    return `${hours} godz.`
  }
  return `${hours},${minutes === 30 ? '5' : minutes} godz.`
}

const CreateReservationFlow: FC = () => {
  const { formatMessage } = useIntl()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const [step, setStep] = useState<0 | 1>(0)
  const [space, setSpace] = useState<CommonRoom | null>(null)
  const [reservationDate, setReservationDate] = useState<dayjs.Dayjs | null>(dayjs().add(1, 'day'))

  const { data: commonRooms = [] } = useCommonRoomsList()
  const { data: availability, isLoading } = useCommonRoomAvailability(
    space?.id ?? 0,
    reservationDate?.format('YYYY-MM-DD')
  )
  const { mutateAsync: createReservation } = useCreateCommonRoomReservation()
  const { data: profile } = useUsersProfile()
  const { refetch } = useCommonRoomsReservationsList()

  const {
    values,
    setFieldValue,
    handleSubmit,
    errors,
    touched,
    handleChange,
    isSubmitting
  } = useFormik<{
    reservationSlot: string | null
    rulesAccepted: boolean
    slotsDuration: number
  }>({
    initialValues: {
      reservationSlot: null,
      rulesAccepted: false,
      slotsDuration: 0
    },
    validationSchema: Yup.object().shape({
      rulesAccepted: Yup.bool().oneOf(
        [true],
        formatMessage({
          id: 'validation.consent',
          defaultMessage: 'Wymagane zgoda'
        })
      )
    }),
    onSubmit: async ({ reservationSlot, rulesAccepted }, { setSubmitting }) => {
      setSubmitting(true)
      try {
        const slot = availableSlots.find((slot) => slot.id === reservationSlot)

        if (!!space && !!slot) {
          await createReservation({
            commonRoom: space.id,
            startDatetime: slot.startDate.format('YYYY-MM-DDTHH:mm:ss'),
            endDatetime: slot.endDate.format('YYYY-MM-DDTHH:mm:ss'),
            contactName: `${profile?.firstName} ${profile?.lastName}`,
            contactEmail: profile?.email || '',
            contactPhone: profile?.mobile || undefined,
            rulesAccepted
          })
        }

        await refetch()

        enqueueSnackbar(
          formatMessage({
            id: 'commonRoomReservations.createReservationSuccess',
            defaultMessage: 'Rezerwacja została utworzona'
          }),
          {
            variant: 'success'
          }
        )
        navigate('/common-room-reservations')
      } catch (error) {
        enqueueSnackbar(
          formatMessage({
            id: 'commonRoomReservations.createReservationError',
            defaultMessage: 'Nie można utworzyć rezerwacji'
          }),
          {
            variant: 'error'
          }
        )
      } finally {
        setSubmitting(false)
      }
    }
  })

  // Generates bookable start slots based on chosen duration and the availability schedule.
  // The 15-minute step produces overlapping options (8:00, 8:15, 8:30...).
  const generateAvailableSlots = (
    selectedDuration: number,
    availabilitySlots: { start: string; end: string }[],
    stepIntervalMinutes?: number
  ) => {
    const slots: {
      id: string
      startDate: dayjs.Dayjs
      endDate: dayjs.Dayjs
      duration: number
      startTime: string
      endTime: string
      available: boolean
    }[] = []

    if (!availability || !space || availabilitySlots.length === 0) return slots

    const date = dayjs(availability.date)
    const stepInterval = stepIntervalMinutes || space.slotDurationMinutes

    // Find the overall available time ranges (merge consecutive slots)
    const availableRanges: { startTime: dayjs.Dayjs; endTime: dayjs.Dayjs }[] = []

    for (const slot of availabilitySlots) {
      const [startHour, startMinute, startSecond] = slot.start.split(':').map(Number)
      const [endHour, endMinute, endSecond] = slot.end.split(':').map(Number)
      const startTime = date
        .set('hour', startHour)
        .set('minute', startMinute)
        .set('second', startSecond)
      const endTime = date.set('hour', endHour).set('minute', endMinute).set('second', endSecond)

      // Check if this slot connects to the previous range
      if (availableRanges.length > 0) {
        const lastRange = availableRanges[availableRanges.length - 1]
        if (lastRange.endTime.isSame(startTime)) {
          // Extend the last range
          lastRange.endTime = endTime
          continue
        }
      }

      availableRanges.push({ startTime, endTime })
    }

    // Generate slots within each available range
    for (const range of availableRanges) {
      let currentStart = range.startTime

      while (currentStart.add(selectedDuration, 'minutes').isSameOrBefore(range.endTime)) {
        const currentEnd = currentStart.add(selectedDuration, 'minutes')

        slots.push({
          id: uuidv4(),
          startDate: currentStart,
          endDate: currentEnd,
          duration: selectedDuration,
          startTime: currentStart.format('HH:mm:ss'),
          endTime: currentEnd.format('HH:mm:ss'),
          available: true
        })

        currentStart = currentStart.add(stepInterval, 'minutes')
      }
    }

    return slots
  }

  const availableSlotsDuration = useMemo(
    () => (space ? getAvailableSlotsDuration(space) : []),
    [space]
  )

  const availableSlots = useMemo(() => {
    if (!availability) return []

    return generateAvailableSlots(values.slotsDuration, availability.slots, 15)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availability, values.slotsDuration])

  const handleBack = () => {
    if (step === 1) {
      setStep(0)
      return
    }
    navigate('/common-room-reservations')
  }

  const handleContinue = () => {
    if (!space) return
    setFieldValue('slotsDuration', getDefaultSlotsDuration(space))
    setFieldValue('reservationSlot', null)
    setStep(1)
  }

  const stepLabel =
    step === 0
      ? formatMessage({
          id: 'helpdesk.reservationStepPlace',
          defaultMessage: 'Krok 1 z 2 — Wybierz miejsce'
        })
      : formatMessage({
          id: 'helpdesk.reservationStepDate',
          defaultMessage: 'Krok 2 z 2 — Wybierz termin'
        })

  return (
    <>
      <BackNavigationHeader
        onBack={handleBack}
        label={formatMessage({
          id: 'helpdesk.newReservationTitle',
          defaultMessage: 'Nowa rezerwacja'
        })}
      />
      <BackNavigationContent>
        {isDesktop && (
          <Typography sx={{ fontSize: '18px', fontWeight: 700, color: theme.r4r.navy }}>
            {formatMessage({
              id: 'helpdesk.newReservationTitle',
              defaultMessage: 'Nowa rezerwacja'
            })}
          </Typography>
        )}
        <StepIndicator step={step} totalSteps={2} label={stepLabel} />
        {step === 0 && (
          <>
            <Box
              sx={(theme) => ({
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                mt: '12px',
                [theme.breakpoints.down('md')]: {
                  paddingBottom: '96px'
                }
              })}>
              {!commonRooms.length && (
                <Typography sx={{ fontSize: '13px', color: theme.r4r.grey }}>
                  {formatMessage({
                    id: 'commonRoomReservations.noAvailableRooms',
                    defaultMessage: 'Brak dostępnych przestrzeni wspólnych'
                  })}
                </Typography>
              )}
              {commonRooms.map((room) => {
                const isActive = space?.id === room.id
                const roomChips = [
                  formatMessage(
                    { id: 'helpdesk.maxCapacity', defaultMessage: 'Max {count} osób' },
                    { count: room.maxCapacity }
                  ),
                  ...getEquipmentList(room)
                ]
                return (
                  <ButtonBase
                    key={room.id}
                    data-cy={`room-card-${room.id}`}
                    onClick={() => setSpace(room)}
                    sx={{
                      display: 'block',
                      textAlign: 'left',
                      backgroundColor: '#fff',
                      border: `2px solid ${isActive ? theme.r4r.navy : 'transparent'}`,
                      boxShadow: theme.r4r.cardShadow,
                      overflow: 'hidden'
                    }}>
                    <Box
                      sx={{
                        height: '130px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: !!room.mainPhoto
                          ? `url("${room.mainPhoto}") center / cover no-repeat`
                          : `linear-gradient(135deg, ${theme.r4r.navy} 0%, ${theme.r4r.azure} 100%)`
                      }}>
                      {!room.mainPhoto && (
                        <MeetingRoomOutlined sx={{ color: '#fff', fontSize: '40px' }} />
                      )}
                    </Box>
                    <Box sx={{ p: '16px' }}>
                      <Typography
                        sx={{ fontSize: '16px', fontWeight: 700, color: theme.r4r.navy }}>
                        {room.displayName}
                      </Typography>
                      <Typography
                        sx={{ fontSize: '13px', color: theme.r4r.grey, mt: '4px', mb: '12px' }}>
                        {room.displayDescription}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {roomChips.map((label, idx) => (
                          <Chip
                            key={idx}
                            label={label}
                            size='small'
                            sx={{
                              borderRadius: 0,
                              backgroundColor: '#EDEEF3',
                              color: theme.r4r.ink,
                              fontSize: '12px',
                              fontWeight: 500
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </ButtonBase>
                )
              })}
            </Box>
            <MobileBottomFooter>
              <Button
                data-cy='button-choose-room'
                onClick={handleContinue}
                fullWidth
                endIcon={<ArrowForward />}
                sx={primaryButtonSx}
                disabled={!space}>
                {formatMessage({
                  id: 'helpdesk.next',
                  defaultMessage: 'Dalej'
                })}
              </Button>
            </MobileBottomFooter>
          </>
        )}
        {step === 1 && !!space && (
          <>
            <Box
              sx={(theme) => ({
                mt: '12px',
                [theme.breakpoints.down('md')]: {
                  paddingBottom: '96px'
                }
              })}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  backgroundColor: '#fff',
                  boxShadow: theme.r4r.cardShadow,
                  p: '12px 16px'
                }}>
                <Box
                  sx={{
                    width: '40px',
                    height: '40px',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `linear-gradient(135deg, ${theme.r4r.navy} 0%, ${theme.r4r.azure} 100%)`
                  }}>
                  <MeetingRoomOutlined sx={{ color: '#fff', fontSize: '22px' }} />
                </Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: theme.r4r.navy }}>
                  {space.displayName}
                </Typography>
              </Box>
              <FieldLabel>
                {formatMessage({
                  id: 'helpdesk.date',
                  defaultMessage: 'Data'
                })}
              </FieldLabel>
              <Box sx={calendarCardSx}>
                <DateCalendar
                  disablePast
                  value={reservationDate}
                  minDate={dayjs().add(1, 'day')}
                  maxDate={dayjs().add(1, 'month')}
                  onChange={(date) => {
                    setReservationDate(date)
                    setFieldValue('reservationSlot', null)
                  }}
                  sx={calendarSx}
                />
              </Box>
              <FieldLabel>
                {formatMessage({
                  id: 'helpdesk.reservationDuration',
                  defaultMessage: 'Długość rezerwacji'
                })}
              </FieldLabel>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: isDesktop ? 'repeat(6, 1fr)' : 'repeat(4, 1fr)',
                  gap: '8px'
                }}>
                {availableSlotsDuration.map(({ minutes, hours, duration }, index) => (
                  <ButtonBase
                    key={index + duration}
                    onClick={() => {
                      setFieldValue('slotsDuration', duration)
                      setFieldValue('reservationSlot', null)
                    }}
                    sx={optionChipSx(values.slotsDuration === duration)}>
                    {formatDurationLabel(hours, minutes)}
                  </ButtonBase>
                ))}
              </Box>
              {values.slotsDuration < MINIMUM_SLOT_WITH_MAINTENANCE_MINUTES && (
                <Typography sx={{ fontSize: '12px', color: theme.r4r.grey, mt: '8px' }}>
                  {formatMessage(
                    {
                      id: 'helpdesk.maintenanceHint',
                      defaultMessage:
                        'Do wybranych krótszych slotów (mniej niż {minimum} minut) zostanie automatycznie dodany czas obsługi technicznej ({maintenance} minut) — czas potrzebny na przygotowanie pomieszczenia dla kolejnych użytkowników.'
                    },
                    {
                      minimum: MINIMUM_SLOT_WITH_MAINTENANCE_MINUTES,
                      maintenance: MAINTENANCE_MINUTES
                    }
                  )}
                </Typography>
              )}
              <FieldLabel>
                {formatMessage({
                  id: 'helpdesk.startHour',
                  defaultMessage: 'Godzina rozpoczęcia'
                })}
              </FieldLabel>
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: '32px' }}>
                  <CircularProgress size={28} />
                </Box>
              ) : !!availableSlots.length ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: isDesktop ? 'repeat(8, 1fr)' : 'repeat(4, 1fr)',
                    gap: '8px'
                  }}>
                  {availableSlots.map(({ startDate, endDate, available, id }) => (
                    <ButtonBase
                      key={id}
                      disabled={!available}
                      onClick={() => setFieldValue('reservationSlot', id)}
                      title={`${startDate.format('HH:mm')} - ${endDate.format('HH:mm')}`}
                      sx={optionChipSx(values.reservationSlot === id, !available)}>
                      {startDate.format('HH:mm')}
                    </ButtonBase>
                  ))}
                </Box>
              ) : (
                <Typography sx={{ fontSize: '12px', color: theme.r4r.grey }}>
                  {formatMessage({
                    id: 'helpdesk.noAvailableHours',
                    defaultMessage: 'Brak dostępnych godzin w wybranym dniu'
                  })}
                </Typography>
              )}
              <Box sx={{ mt: '20px' }}>
                <FormControlLabel
                  sx={{
                    alignItems: 'flex-start',
                    '.MuiCheckbox-root': {
                      pt: 0,
                      color:
                        !!errors.rulesAccepted && !!touched.rulesAccepted
                          ? theme.r4r.red
                          : undefined
                    }
                  }}
                  control={
                    <Checkbox
                      name='rulesAccepted'
                      checked={values.rulesAccepted}
                      onChange={handleChange}
                    />
                  }
                  label={
                    <>
                      <Typography sx={{ fontSize: '13px', color: theme.r4r.ink }}>
                        {formatMessage({
                          id: 'commonRoomReservations.rulesAccepted',
                          defaultMessage:
                            'Potwierdzam że zapoznałem/am się z regulaminem i zobowiązuję się do niego przestrzegania.'
                        })}
                      </Typography>
                      <Link
                        href={space.rulesPdfUrl}
                        underline='hover'
                        target='_blank'
                        rel='noopener'
                        sx={{ fontSize: '13px', color: theme.r4r.azure }}>
                        {formatMessage({
                          id: 'commonRoomReservations.rulesLink',
                          defaultMessage: 'Pełna treść regulaminu (PDF)'
                        })}
                      </Link>
                    </>
                  }
                />
                {!!errors.rulesAccepted && !!touched.rulesAccepted && (
                  <FormHelperText error>{errors.rulesAccepted}</FormHelperText>
                )}
              </Box>
            </Box>
            <MobileBottomFooter>
              <LoadingButton
                data-cy='button-create-reservation'
                loading={isSubmitting}
                onClick={() => handleSubmit()}
                fullWidth
                endIcon={<Check />}
                sx={submitButtonSx}
                disabled={isSubmitting || !values.reservationSlot}>
                {formatMessage({
                  id: 'helpdesk.reserve',
                  defaultMessage: 'Zarezerwuj'
                })}
              </LoadingButton>
            </MobileBottomFooter>
          </>
        )}
      </BackNavigationContent>
    </>
  )
}

export default CreateReservationFlow
